import { ErrorBoundary } from "@sentry/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "motion/react";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";

import type {
  CreatableTicketCategoryLocationDto,
  CreatableTicketCategoryNodeDto,
  CreatableTicketCategorySearchNodeDto,
  CreatableTicketCategoryV2Dto,
  TicketRequest,
} from "@/api/types";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { Form } from "@/components/Form/Form";
import { Modal, type ModalBaseProps } from "@/components/Modal/Modal";
import { ProgressBar } from "@/components/ProgressBar/ProgressBar";
import { Button } from "@/design-system/Button";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { useUploadVideo } from "@/hooks/Network/useUploadVideo";
import { useSlug } from "@/hooks/useSlug";
import { findNodeInTree } from "@/queries/ticket-categories/helpers";
import { useTicketCategoryQueries } from "@/queries/ticket-categories/queryOptions";
import { ticketMutations } from "@/queries/tickets/mutations";
import {
  APPOINTMENT_PLANNING_MAX_POLL_ATTEMPTS,
  APPOINTMENT_PLANNING_POLL_INTERVAL_MS,
  useTicketQueries,
} from "@/queries/tickets/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { getPathProgress, usePotentialDuplicateTickets } from "../helpers";
import type { CreateTicketModalFormValues } from "../types";
import { CreateTicketModalStep } from "../types";
import { CreateTicketModalAdminSteps } from "./CreateTicketModalAdminSteps";
import { CreateTicketModalAppointmentSteps } from "./CreateTicketModalAppointmentSteps";
import { CreateTicketModalBreadcrumbs } from "./CreateTicketModalBreadcrumbs";
import { CreateTicketModalDecisionTreeSteps } from "./CreateTicketModalDecisionTreeSteps";
import { CreateTicketModalSharedSteps } from "./CreateTicketModalSharedSteps";

const FORM_ID = "create-ticket-modal-form";

type CreateTicketModalProps = ModalBaseProps & {
  isCreatorResident: boolean;
};

export function CreateTicketModal({
  isOpened,
  onOpenChange,
  isCreatorResident,
}: CreateTicketModalProps): React.ReactNode {
  const defaultStep = isCreatorResident ? CreateTicketModalStep.SELECT_CATEGORY : CreateTicketModalStep.SELECT_REPORTER;
  const [step, setStep] = useState<CreateTicketModalStep>(defaultStep);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const slug = useSlug();
  const sessionUser = useSessionUser();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();

  const defaultFormValues: CreateTicketModalFormValues = useMemo(
    () => ({
      title: "",
      content: "",
      category: undefined,
      visibility: "private",
      images: [],
      videos: [],
      isRegulationsAccepted: false,
      phoneNumber: isCreatorResident ? sessionUser.phoneNumber : undefined,
      tree: {
        location: undefined,
        path: [],
      },
      reporter: undefined,
      assignee: undefined,
    }),
    [isCreatorResident, sessionUser.phoneNumber],
  );
  const form = useForm<CreateTicketModalFormValues>({
    defaultValues: defaultFormValues,
    mode: "onChange",
  });
  const currCategory = useWatch({
    name: "category",
    control: form.control,
  });
  const currTree = useWatch({
    name: "tree",
    control: form.control,
  });
  const currLeafNode = currTree.path[currTree.path.length - 1];
  const currImages = useWatch({
    name: "images",
    control: form.control,
  });
  const currVideos = useWatch({
    name: "videos",
    control: form.control,
  });
  const currReporter = useWatch({
    name: "reporter",
    control: form.control,
  });
  // If the ticket creator is a resident, we check if the creator's phone number is available
  // If the ticket creator is not a resident, we check if the selected reporter's phone number is available
  const isPhoneNumberAvailable =
    (isCreatorResident && Boolean(sessionUser.phoneNumber)) ||
    (!isCreatorResident && Boolean(currReporter?.phoneNumber));

  const ticketCategoryQueries = useTicketCategoryQueries();
  const {
    data: potentialDuplicateTicketsData,
    isFetching: isFetchingPotentialDuplicateTicketsData,
    refetch: refetchPotentialDuplicatedTicketsData,
  } = usePotentialDuplicateTickets({
    isEnabled: isOpened,
  });
  const addTicketMutation = ticketMutations.useAddTicket();
  const { uploadFormImage, isUploadingImage } = useUploadImage();
  const { uploadFormVideo, abortUploadVideos } = useUploadVideo({});

  // Appointment flow (see AOP-9072). The ticket is created the moment the resident
  // confirms a slot; the preview is a post-creation confirmation. Skipping / no-options / a failed
  // planning create the ticket later from the preview with a planning id but no slot.
  const ticketQueries = useTicketQueries();
  const startAppointmentPlanningMutation = ticketMutations.useStartAppointmentPlanning();
  const [appointmentPlanningId, setAppointmentPlanningId] = useState<string>();
  const [appointmentPreparationFailed, setAppointmentPreparationFailed] = useState(false);
  const [appointmentSelectedSlotId, setAppointmentSelectedSlotId] = useState<string>();
  const [appointmentRemovedSlotIds, setAppointmentRemovedSlotIds] = useState<string[]>([]);
  const [appointmentCreatedTicketId, setAppointmentCreatedTicketId] = useState<string>();
  const [isAppointmentMomentUnavailableShown, setIsAppointmentMomentUnavailableShown] = useState(false);
  const [isAppointmentSkipDialogOpened, setIsAppointmentSkipDialogOpened] = useState(false);
  const appointmentUploadedMediaIds = useRef<{ imageIds: string[]; videoIds: string[] }>({
    imageIds: [],
    videoIds: [],
  });
  const appointmentPlanningStartedRef = useRef(false);
  const appointmentPlanningTimeoutRef = useRef<number | undefined>(undefined);
  // Identifies the active planning run. Bumped whenever the modal closes so that late upload,
  // mutation, and timeout callbacks from a superseded run can detect they are stale and bail
  // instead of mutating state that now belongs to a fresh run started on reopen.
  const appointmentPlanningRunIdRef = useRef(0);
  const { data: appointmentPlanning } = useQuery(ticketQueries.appointmentPlanning(appointmentPlanningId));
  const appointmentOptions = (appointmentPlanning?.options ?? []).filter(
    (option) => !appointmentRemovedSlotIds.includes(option.slotId),
  );
  const appointmentSelectedOption = appointmentPlanning?.options.find(
    (option) => option.slotId === appointmentSelectedSlotId,
  );

  useEffect(() => {
    if (isOpened) {
      form.reset();
      setStep(defaultStep);
      // Reset the appointment flow state so a reopened modal starts a fresh planning.
      setAppointmentPlanningId(undefined);
      setAppointmentPreparationFailed(false);
      setAppointmentSelectedSlotId(undefined);
      setAppointmentRemovedSlotIds([]);
      setAppointmentCreatedTicketId(undefined);
      setIsAppointmentMomentUnavailableShown(false);
      appointmentUploadedMediaIds.current = { imageIds: [], videoIds: [] };
      appointmentPlanningStartedRef.current = false;
    } else {
      // On close, invalidate any in-flight planning run and cancel its safety-net timeout so their
      // late callbacks cannot mutate state that a reopened modal's fresh run now owns.
      appointmentPlanningRunIdRef.current += 1;
      if (appointmentPlanningTimeoutRef.current !== undefined) {
        window.clearTimeout(appointmentPlanningTimeoutRef.current);
        appointmentPlanningTimeoutRef.current = undefined;
      }
    }
  }, [defaultStep, form, isOpened, refetchPotentialDuplicatedTicketsData]);

  // Prefetch ticket categories if initial step is not `SELECT_CATEGORY`
  useEffect(() => {
    if (
      isOpened &&
      defaultStep !== CreateTicketModalStep.SELECT_CATEGORY &&
      queryClient.getQueryState(ticketCategoryQueries.listCategoryCreatables().queryKey) === undefined
    ) {
      void queryClient.prefetchQuery(ticketCategoryQueries.listCategoryCreatables());
    }
  }, [isOpened, defaultStep, queryClient, ticketCategoryQueries]);

  const isPhoneNumberRequired = currLeafNode
    ? currLeafNode.nodeInfo?.requirePhoneNumber
    : currCategory?.categoryInfo?.requirePhoneNumber;
  // The leaf node's flag wins over the category's, mirroring how `isPhoneNumberRequired` resolves.
  const appointmentSelection = currLeafNode?.nodeInfo?.appointments ?? currCategory?.categoryInfo?.appointments;
  const isAppointmentFlow = appointmentSelection?.canSelect === true;

  const getDecisionTreeFlowNextStep = ({ isLeafNodeSelected } = { isLeafNodeSelected: false }) => {
    switch (step) {
      case CreateTicketModalStep.SELECT_REPORTER:
        return CreateTicketModalStep.SELECT_CATEGORY;

      case CreateTicketModalStep.SELECT_CATEGORY:
        return CreateTicketModalStep.SELECT_TREE_LOCATION;

      case CreateTicketModalStep.SELECT_TREE_LOCATION:
        return CreateTicketModalStep.SELECT_TREE_ROOT_NODE;

      case CreateTicketModalStep.SELECT_TREE_ROOT_NODE: {
        if (shouldShowPotentialDuplicateTickets) {
          return CreateTicketModalStep.VIEW_POTENTIAL_DUPLICATE_TICKETS;
        } else {
          return CreateTicketModalStep.SELECT_TREE_NODE;
        }
      }

      case CreateTicketModalStep.VIEW_POTENTIAL_DUPLICATE_TICKETS:
        return CreateTicketModalStep.SELECT_TREE_NODE;

      case CreateTicketModalStep.SELECT_TREE_NODE: {
        if (isLeafNodeSelected) {
          return CreateTicketModalStep.ADD_ATTACHMENTS;
        } else {
          return CreateTicketModalStep.SELECT_TREE_NODE;
        }
      }

      case CreateTicketModalStep.ADD_ATTACHMENTS:
        return CreateTicketModalStep.ADD_INFORMATION;

      case CreateTicketModalStep.ADD_INFORMATION: {
        if (isPhoneNumberRequired && !isPhoneNumberAvailable) {
          return CreateTicketModalStep.ADD_CONTACT_INFORMATION;
        } else {
          return isAppointmentFlow ? CreateTicketModalStep.APPOINTMENT_LOADING : CreateTicketModalStep.CONFIRM;
        }
      }

      case CreateTicketModalStep.ADD_CONTACT_INFORMATION:
        return isAppointmentFlow ? CreateTicketModalStep.APPOINTMENT_LOADING : CreateTicketModalStep.CONFIRM;

      case CreateTicketModalStep.CONFIRM:
      default:
        return undefined;
    }
  };

  const getDecisionTreeFlowPreviousStep = ({ isOnlyRootNodeSelected } = { isOnlyRootNodeSelected: false }) => {
    switch (step) {
      case CreateTicketModalStep.SELECT_CATEGORY:
        return CreateTicketModalStep.SELECT_REPORTER;

      case CreateTicketModalStep.SELECT_TREE_LOCATION:
        return CreateTicketModalStep.SELECT_CATEGORY;

      case CreateTicketModalStep.SELECT_TREE_ROOT_NODE:
        return CreateTicketModalStep.SELECT_TREE_LOCATION;

      case CreateTicketModalStep.VIEW_POTENTIAL_DUPLICATE_TICKETS:
        return CreateTicketModalStep.SELECT_TREE_ROOT_NODE;

      case CreateTicketModalStep.SELECT_TREE_NODE:
        if (isOnlyRootNodeSelected && shouldShowPotentialDuplicateTickets) {
          return CreateTicketModalStep.VIEW_POTENTIAL_DUPLICATE_TICKETS;
        } else if (isOnlyRootNodeSelected) {
          return CreateTicketModalStep.SELECT_TREE_ROOT_NODE;
        } else {
          return CreateTicketModalStep.SELECT_TREE_NODE;
        }
      case CreateTicketModalStep.ADD_ATTACHMENTS:
        return CreateTicketModalStep.SELECT_TREE_NODE;

      case CreateTicketModalStep.ADD_INFORMATION:
        return CreateTicketModalStep.ADD_ATTACHMENTS;

      case CreateTicketModalStep.ADD_CONTACT_INFORMATION:
        return CreateTicketModalStep.ADD_INFORMATION;

      case CreateTicketModalStep.CONFIRM: {
        if (isPhoneNumberRequired && !isPhoneNumberAvailable) {
          return CreateTicketModalStep.ADD_CONTACT_INFORMATION;
        } else {
          return CreateTicketModalStep.ADD_INFORMATION;
        }
      }

      case CreateTicketModalStep.SELECT_REPORTER:
      default:
        return undefined;
    }
  };

  const getRegularFlowNextStep = () => {
    switch (step) {
      case CreateTicketModalStep.SELECT_REPORTER:
        return CreateTicketModalStep.SELECT_CATEGORY;

      case CreateTicketModalStep.SELECT_CATEGORY:
        return CreateTicketModalStep.ADD_ATTACHMENTS;

      case CreateTicketModalStep.ADD_ATTACHMENTS:
        return CreateTicketModalStep.ADD_INFORMATION;

      case CreateTicketModalStep.ADD_INFORMATION: {
        if (isPhoneNumberRequired && !isPhoneNumberAvailable) {
          return CreateTicketModalStep.ADD_CONTACT_INFORMATION;
        } else {
          return isAppointmentFlow ? CreateTicketModalStep.APPOINTMENT_LOADING : CreateTicketModalStep.CONFIRM;
        }
      }

      case CreateTicketModalStep.ADD_CONTACT_INFORMATION:
        return isAppointmentFlow ? CreateTicketModalStep.APPOINTMENT_LOADING : CreateTicketModalStep.CONFIRM;

      case CreateTicketModalStep.CONFIRM:
      default:
        return undefined;
    }
  };

  const getRegularFlowPreviousStep = () => {
    switch (step) {
      case CreateTicketModalStep.SELECT_CATEGORY:
        return CreateTicketModalStep.SELECT_REPORTER;

      case CreateTicketModalStep.ADD_ATTACHMENTS:
        return CreateTicketModalStep.SELECT_CATEGORY;

      case CreateTicketModalStep.ADD_INFORMATION:
        return CreateTicketModalStep.ADD_ATTACHMENTS;

      case CreateTicketModalStep.CONFIRM: {
        if (isPhoneNumberRequired && !isPhoneNumberAvailable) {
          return CreateTicketModalStep.ADD_CONTACT_INFORMATION;
        } else {
          return CreateTicketModalStep.ADD_INFORMATION;
        }
      }

      case CreateTicketModalStep.ADD_CONTACT_INFORMATION:
        return CreateTicketModalStep.ADD_INFORMATION;

      case CreateTicketModalStep.SELECT_REPORTER:
      default:
        return undefined;
    }
  };

  const handleClickBack = () => {
    let newStep: CreateTicketModalStep | undefined;

    if (currCategory?.tree) {
      const currPath = [...currTree.path];
      const isOnlyRootNodeSelected = currPath.length === 1;

      newStep = getDecisionTreeFlowPreviousStep({
        isOnlyRootNodeSelected,
      });

      // Pop node from the selected path if we are going back to a node selection step
      if (
        newStep === CreateTicketModalStep.SELECT_TREE_NODE ||
        newStep === CreateTicketModalStep.SELECT_TREE_ROOT_NODE
      ) {
        currPath.pop();
        form.setValue("tree.path", currPath);
      }
    } else {
      newStep = getRegularFlowPreviousStep();
    }

    if (newStep) {
      setStep(newStep);
    }
  };

  const handleConfirmAddImages = () => {
    const newStep = getDecisionTreeFlowNextStep();

    if (newStep) {
      setStep(newStep);
    }
  };

  const handleConfirmAddInformation = async () => {
    const newStep = getRegularFlowNextStep();
    const isValid = await form.trigger(["title", "content"]);

    if (isValid && newStep) {
      setStep(newStep);
    }
  };

  const handleConfirmAddContactInformation = async () => {
    const newStep = getRegularFlowNextStep();
    const isValid = await form.trigger("phoneNumber");

    if (isValid && newStep) {
      setStep(newStep);
    }
  };

  const handleConfirmSelectReporter = async () => {
    const newStep = getDecisionTreeFlowNextStep();
    const isValid = await form.trigger("reporter");

    if (isValid && newStep) {
      setStep(newStep);
    }
  };

  const handleSelectCategory = (category: CreatableTicketCategoryV2Dto) => {
    form.setValue("category", category, {
      shouldDirty: true,
    });

    let newStep: CreateTicketModalStep | undefined;
    if (category.tree) {
      newStep = getDecisionTreeFlowNextStep();
    } else {
      newStep = getRegularFlowNextStep();
    }

    if (newStep) {
      setStep(newStep);
    }
  };

  const handleSelectLocation = (location: CreatableTicketCategoryLocationDto) => {
    form.setValue("tree.location", location, {
      shouldDirty: true,
    });

    const newStep = getDecisionTreeFlowNextStep();
    if (newStep) {
      setStep(newStep);
    }
  };

  const handleSelectNode = (node: CreatableTicketCategoryNodeDto | CreatableTicketCategorySearchNodeDto) => {
    const path = form.getValues("tree.path");
    const newPath = [...path, node];
    const isLeafNode = node.type === "leaf" || node.type === "leafOther";

    form.setValue("tree.path", newPath, {
      shouldDirty: true,
    });

    if (isLeafNode) {
      if (node.type === "leaf") {
        // If the selected node is a leaf, we set the title to the node name
        form.setValue("title", node.name, {
          shouldDirty: true,
          shouldValidate: true,
        });
        form.resetField("content");
      } else if (node.type === "leafOther") {
        // If the selected node is a leafOther, reset title and content fields
        form.resetField("title");
        form.resetField("content");
      }

      // If node is a leaf node from search result (`CreatableTicketCategorySearchNodeDto`)
      // We need to construct thefull path to the node
      if ("parents" in node) {
        const rootNodes =
          currCategory?.tree?.locations?.find((location) => location.location === currTree.location?.location)?.nodes ||
          [];
        const targetRootNode = rootNodes.find((rootNode) => rootNode.id === node.parents[0].id);

        if (targetRootNode) {
          const parentPath: CreatableTicketCategoryNodeDto[] = node.parents.reduce<CreatableTicketCategoryNodeDto[]>(
            (acc, curr, index) => {
              if (index === 0) {
                acc.push(targetRootNode);

                return acc;
              }

              // Find current parent node in the tree
              const currNode = findNodeInTree<CreatableTicketCategoryNodeDto>(targetRootNode.children, curr.id);
              if (currNode) {
                acc.push(currNode);
              }

              return acc;
            },
            [],
          );

          form.setValue("tree.path", [...parentPath, node], {
            shouldDirty: true,
          });
        }
      }
    }

    const newStep = getDecisionTreeFlowNextStep({
      isLeafNodeSelected: isLeafNode,
    });
    if (newStep) {
      setStep(newStep);
    }
  };

  const buildTicketPayload = (formValues: CreateTicketModalFormValues): TicketRequest => ({
    title: formValues.title,
    content: formValues.content,
    visibility: formValues.visibility,
    categoryId: formValues.category?.id,
    categoryNodeId: currTree.path[currTree.path.length - 1]?.id,
    requestOrigin: "web",
    locationType: currTree.location?.location,
    reporterId: formValues.reporter?.id,
    assigneeId: formValues.assignee?.id,
    // Strip away all empty spaces used for formatting
    phoneNumber: formValues.phoneNumber?.replaceAll(" ", "") || undefined,
  });

  const uploadMedia = async (formValues: CreateTicketModalFormValues) => {
    const imageUploadPromises = formValues.images.map((image) => uploadFormImage(image));
    // Currently, FE only allows 1 video
    const videoUploadPromises = formValues.videos.map((video) => uploadFormVideo(video));

    const imageIds = (await Promise.allSettled(imageUploadPromises))
      .map((image) => (image.status === "fulfilled" ? image.value?.id : ""))
      .filter(Boolean) as string[];
    const videoIds = (await Promise.allSettled(videoUploadPromises))
      .map((video) => (video.status === "fulfilled" ? video.value?.id : ""))
      .filter(Boolean) as string[];

    const isFullyUploaded =
      imageIds.length === formValues.images.length && videoIds.length === formValues.videos.length;

    return { imageIds, videoIds, isFullyUploaded };
  };

  const handleSubmit = async (formValues: CreateTicketModalFormValues) => {
    const { imageIds, videoIds, isFullyUploaded } = await uploadMedia(formValues);

    if (isFullyUploaded) {
      addTicketMutation.mutate(
        {
          ...buildTicketPayload(formValues),
          imageIds,
          videoIds,
        },
        {
          onSuccess: (result) => {
            void navigate(routes.tickets.details({ slug, id: result.data.id }));
            onOpenChange(false);
          },
        },
      );
    } else {
      showFlashToast({
        variant: "alert",
        message: t("page.resident-tickets.notification.error"),
      });
    }
  };

  // Create the ticket for the appointment flow. `slotId` present = the resident confirmed a
  // moment (create-on-confirm); absent = they skipped or no moment was available (create from the
  // preview so appointment contacts them to plan).
  const createAppointmentTicket = (slotId?: string) => {
    const formValues = form.getValues();

    addTicketMutation.mutate(
      {
        ...buildTicketPayload(formValues),
        imageIds: appointmentUploadedMediaIds.current.imageIds,
        videoIds: appointmentUploadedMediaIds.current.videoIds,
        appointmentPlanningId: appointmentPlanningId,
        appointmentSlotId: slotId,
      },
      {
        onSuccess: (result) => {
          if (slotId) {
            setAppointmentCreatedTicketId(result.data.id);
            setStep(CreateTicketModalStep.APPOINTMENT_PREVIEW);
          } else {
            void navigate(routes.tickets.details({ slug, id: result.data.id }));
            onOpenChange(false);
          }
        },
        onError: slotId
          ? () => {
              // The moment was taken between planning and confirming: drop it and let them re-pick.
              setAppointmentRemovedSlotIds((prev) => [...prev, slotId]);
              setAppointmentSelectedSlotId(undefined);
              setIsAppointmentMomentUnavailableShown(true);
            }
          : undefined,
      },
    );
  };

  const handleConfirmAppointmentMoment = () => {
    if (!appointmentSelectedSlotId) {
      return;
    }

    createAppointmentTicket(appointmentSelectedSlotId);
  };

  const handleSkipAppointmentMoment = () => {
    setIsAppointmentSkipDialogOpened(true);
  };

  const handleConfirmSkipAppointmentMoment = () => {
    setIsAppointmentSkipDialogOpened(false);
    setStep(CreateTicketModalStep.APPOINTMENT_PREVIEW);
  };

  const handleConfirmAppointmentPreview = () => {
    if (appointmentCreatedTicketId) {
      void navigate(routes.tickets.details({ slug, id: appointmentCreatedTicketId }));
      onOpenChange(false);
    } else {
      createAppointmentTicket(undefined);
    }
  };

  // Start the appointment planning once when the resident lands on the loading step: upload the
  // media (reused at create so it is never uploaded twice), then kick off the planning job.
  useEffect(() => {
    if (step !== CreateTicketModalStep.APPOINTMENT_LOADING || appointmentPlanningStartedRef.current) {
      return;
    }

    appointmentPlanningStartedRef.current = true;
    // Tie every async callback below to this run so a close/reopen (which bumps the run id) makes
    // this run's stale continuations bail instead of clobbering the fresh run.
    const runId = appointmentPlanningRunIdRef.current;

    const startPlanning = async () => {
      const formValues = form.getValues();
      const { imageIds, videoIds, isFullyUploaded } = await uploadMedia(formValues);

      if (appointmentPlanningRunIdRef.current !== runId) {
        return;
      }

      // A partial media upload must not create a planning with missing attachments. Send the
      // resident back to the attachments step to retry, mirroring the regular submit flow, and let
      // the effect start a fresh planning once they return to the loading step.
      if (!isFullyUploaded) {
        appointmentPlanningStartedRef.current = false;
        showFlashToast({
          variant: "alert",
          message: t("page.resident-tickets.notification.error"),
        });
        setStep(CreateTicketModalStep.ADD_ATTACHMENTS);

        return;
      }

      appointmentUploadedMediaIds.current = { imageIds, videoIds };

      startAppointmentPlanningMutation.mutate(
        {
          categoryId: formValues.category?.id ?? "",
          categoryNodeId: currTree.path[currTree.path.length - 1]?.id,
          title: formValues.title,
          content: formValues.content,
          imageIds,
        },
        {
          onSuccess: (planning) => {
            if (appointmentPlanningRunIdRef.current !== runId) {
              return;
            }

            setAppointmentPlanningId(planning.planningId);

            // Safety net: start the timeout only now that the planning exists, so it measures the
            // polling window itself. Starting it before upload/create would let slow media eat into
            // the window and skip to the preview before options arrive.
            appointmentPlanningTimeoutRef.current = window.setTimeout(
              () => {
                if (appointmentPlanningRunIdRef.current !== runId) {
                  return;
                }

                setAppointmentPreparationFailed(true);
                setStep((current) =>
                  current === CreateTicketModalStep.APPOINTMENT_LOADING
                    ? CreateTicketModalStep.APPOINTMENT_PREVIEW
                    : current,
                );
              },
              APPOINTMENT_PLANNING_MAX_POLL_ATTEMPTS * APPOINTMENT_PLANNING_POLL_INTERVAL_MS + 2000,
            );
          },
          // A failed planning must not trap the resident: fall through to the preview so they can
          // still submit and the repair team will contact them to plan.
          onError: () => {
            if (appointmentPlanningRunIdRef.current !== runId) {
              return;
            }

            setAppointmentPreparationFailed(true);
            setStep(CreateTicketModalStep.APPOINTMENT_PREVIEW);
          },
        },
      );
    };

    void startPlanning();

    return () => {
      if (appointmentPlanningTimeoutRef.current !== undefined) {
        window.clearTimeout(appointmentPlanningTimeoutRef.current);
        appointmentPlanningTimeoutRef.current = undefined;
      }
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Advance out of the loading step once the planning reaches a terminal state.
  useEffect(() => {
    if (step !== CreateTicketModalStep.APPOINTMENT_LOADING || !appointmentPlanning) {
      return;
    }

    if (appointmentPlanning.state === "awaitingResidentSelection") {
      setStep(
        appointmentPlanning.options.length > 0
          ? CreateTicketModalStep.APPOINTMENT_PICK_MOMENT
          : CreateTicketModalStep.APPOINTMENT_PREVIEW,
      );
    } else if (appointmentPlanning.state === "manualFollowUp" || appointmentPlanning.state === "failed") {
      setStep(CreateTicketModalStep.APPOINTMENT_PREVIEW);
    }
  }, [step, appointmentPlanning]);

  // If the resident removes every proposed moment via "moment not available", fall through to the
  // preview so they can still submit without a slot.
  useEffect(() => {
    if (
      step === CreateTicketModalStep.APPOINTMENT_PICK_MOMENT &&
      appointmentPlanning &&
      appointmentPlanning.options.length > 0 &&
      appointmentOptions.length === 0
    ) {
      setStep(CreateTicketModalStep.APPOINTMENT_PREVIEW);
    }
  }, [step, appointmentPlanning, appointmentOptions.length]);

  const handleIgnorePotentialDuplicateTickets = () => {
    setStep(CreateTicketModalStep.SELECT_TREE_NODE);
  };

  const handleCloseModal = () => {
    const isFormDirty = form.formState.isDirty;

    if (isFormDirty) {
      const confirmLeave = window.confirm(t("page.resident-tickets.create-modal.confirm-leave"));
      if (!confirmLeave) return;
    }

    onOpenChange(false);
  };

  let addInformationDescription: string;
  if (currTree.path.length === 0) {
    addInformationDescription = isCreatorResident
      ? t("page.resident-tickets.create-modal.description.add-information.with-custom-subject")
      : t("page.admin-tickets.create-modal.description.add-information.with-custom-subject");
  } else {
    addInformationDescription = isCreatorResident
      ? t("page.resident-tickets.create-modal.description.add-information.with-generated-subject")
      : t("page.admin-tickets.create-modal.description.add-information.with-generated-subject");
  }
  let addAttachmentsDescription: string;
  if (currCategory?.categoryInfo?.requireImages && !currCategory?.categoryInfo?.allowVideos) {
    addAttachmentsDescription = isCreatorResident
      ? t("page.resident-tickets.create-modal.form.images.description.required")
      : t("page.admin-tickets.create-modal.form.images.description.required");
  } else {
    addAttachmentsDescription = isCreatorResident
      ? t("page.resident-tickets.create-modal.description.add-attachment")
      : t("page.admin-tickets.create-modal.description.add-attachment");
  }

  const modalConfig: Record<
    CreateTicketModalStep,
    {
      title: string;
      description?: string;
    }
  > = {
    [CreateTicketModalStep.SELECT_REPORTER]: {
      title: t("page.admin-tickets.create-modal.select-reporter"),
      description: t("page.admin-tickets.create-modal.description.select-reporter"),
    },
    [CreateTicketModalStep.SELECT_CATEGORY]: {
      title: isCreatorResident
        ? t("page.resident-tickets.create-modal.title.select-category")
        : t("page.admin-tickets.create-modal.select-category"),
    },
    [CreateTicketModalStep.SELECT_TREE_LOCATION]: {
      title: isCreatorResident
        ? t("page.resident-tickets.create-modal.title.select-location")
        : t("page.admin-tickets.create-modal.select-location"),
    },
    [CreateTicketModalStep.SELECT_TREE_ROOT_NODE]: {
      title: isCreatorResident
        ? t("page.resident-tickets.create-modal.title.select-specific-issue")
        : t("page.admin-tickets.create-modal.select-specific-issue"),
      description: isCreatorResident
        ? t("page.resident-tickets.create-modal.description.select-specific-issue")
        : t("page.admin-tickets.create-modal.description.select-specific-issue"),
    },
    [CreateTicketModalStep.VIEW_POTENTIAL_DUPLICATE_TICKETS]: {
      title: t("page.resident-tickets.create-modal.title.title.view-existing-tickets"),
      description: t("page.resident-tickets.create-modal.description.view-existing-tickets"),
    },
    [CreateTicketModalStep.SELECT_TREE_NODE]: {
      title: isCreatorResident
        ? t("page.resident-tickets.create-modal.title.select-specific-issue")
        : t("page.admin-tickets.create-modal.select-specific-issue"),
      description: isCreatorResident
        ? t("page.resident-tickets.create-modal.description.select-specific-issue")
        : t("page.admin-tickets.create-modal.description.select-specific-issue"),
    },
    [CreateTicketModalStep.ADD_ATTACHMENTS]: {
      title: isCreatorResident
        ? t("page.resident-tickets.create-modal.title.add-attachment")
        : t("page.admin-tickets.create-modal.title.add-attachment"),
      description: addAttachmentsDescription,
    },
    [CreateTicketModalStep.ADD_INFORMATION]: {
      title: isCreatorResident
        ? t("page.resident-tickets.create-modal.title.title.add-information")
        : t("page.admin-tickets.create-modal.title.add-information"),
      description: addInformationDescription,
    },
    [CreateTicketModalStep.ADD_CONTACT_INFORMATION]: {
      title: isCreatorResident
        ? t("page.resident-tickets.create-modal.title.title.add-contact-information")
        : t("page.admin-tickets.create-modal.title.add-contact-information"),
    },
    [CreateTicketModalStep.CONFIRM]: {
      title: isCreatorResident
        ? t("page.resident-tickets.create-modal.title.title.confirm")
        : t("page.admin-tickets.create-modal.title.confirm"),
    },
    [CreateTicketModalStep.APPOINTMENT_LOADING]: {
      title: t("page.resident-tickets.create-modal.appointment.pick.title"),
    },
    [CreateTicketModalStep.APPOINTMENT_PICK_MOMENT]: {
      title: t("page.resident-tickets.create-modal.appointment.pick.title"),
    },
    [CreateTicketModalStep.APPOINTMENT_PREVIEW]: {
      title: t("page.resident-tickets.create-modal.appointment.preview.title"),
    },
  };
  const potentialDuplicateTickets = useMemo(
    () => potentialDuplicateTicketsData?.pages?.flatMap((page) => page.items) || [],
    [potentialDuplicateTicketsData],
  );
  const currProgress = useMemo(() => {
    const progressMap: Partial<Record<CreateTicketModalStep, number>> = {
      [CreateTicketModalStep.SELECT_TREE_LOCATION]: 30,
      [CreateTicketModalStep.SELECT_TREE_ROOT_NODE]: 40,
      [CreateTicketModalStep.VIEW_POTENTIAL_DUPLICATE_TICKETS]: 40,
      [CreateTicketModalStep.SELECT_TREE_NODE]:
        currTree.path.length === 0
          ? 40 // Should never happen, but just in case
          : 40 + getPathProgress(currTree.path[currTree.path.length - 1], currTree.path.length) * 0.4,
      [CreateTicketModalStep.ADD_ATTACHMENTS]: 70,
      [CreateTicketModalStep.ADD_INFORMATION]: 90,
      [CreateTicketModalStep.ADD_CONTACT_INFORMATION]: 95,
      [CreateTicketModalStep.CONFIRM]: 100,
      [CreateTicketModalStep.APPOINTMENT_LOADING]: 95,
      [CreateTicketModalStep.APPOINTMENT_PICK_MOMENT]: 95,
      [CreateTicketModalStep.APPOINTMENT_PREVIEW]: 100,
    };

    return progressMap[step] || 0;
  }, [currTree.path, step]);
  const shouldShowPotentialDuplicateTickets =
    isCreatorResident && currTree?.location?.location === "outside" && potentialDuplicateTickets.length > 0;
  const isBreadcrumbsShown = useMemo(
    () =>
      step === CreateTicketModalStep.SELECT_TREE_NODE ||
      step === CreateTicketModalStep.SELECT_TREE_ROOT_NODE ||
      (currCategory?.tree && step === CreateTicketModalStep.ADD_INFORMATION),
    [step, currCategory?.tree],
  );
  const isLoading = isFetchingPotentialDuplicateTicketsData;

  return (
    <Modal.Root
      title={modalConfig[step].title}
      description={modalConfig[step].description}
      data-testid="create-ticket-modal"
      isOpened={isOpened}
      onOpenChange={(state) => {
        if (state) {
          onOpenChange(state);
        } else {
          handleCloseModal();
        }
      }}
      shouldCloseOnClickOutside={false}
      shouldCloseOnEsc={false}
      height={680}
      onExitComplete={() => abortUploadVideos(form.getValues("videos"))}
    >
      <ErrorBoundary
        fallback={() => (
          <section className="flex flex-1 flex-col items-center justify-center gap-space-md">
            <span className="text-caption text-text-secondary">{t("common.generic-error")}</span>
          </section>
        )}
      >
        <Modal.Body>
          <Form.Root fullWidth id={FORM_ID} formMethods={form} onSubmit={handleSubmit}>
            {isLoading ? (
              <section className="flex w-full flex-1 items-center justify-center">
                <FullSizeLoader size="sm" noDelay />
              </section>
            ) : (
              <section className="flex w-full flex-1 flex-col gap-space-sm">
                {/* Breadcrumbs */}
                {isBreadcrumbsShown && <CreateTicketModalBreadcrumbs onChangeStep={setStep} />}
                {/* Steps */}
                {step === CreateTicketModalStep.SELECT_REPORTER && <CreateTicketModalAdminSteps.SelectReporter />}
                {step === CreateTicketModalStep.SELECT_CATEGORY && (
                  <CreateTicketModalSharedSteps.SelectCategory onSelectTicketCategory={handleSelectCategory} />
                )}
                {step === CreateTicketModalStep.SELECT_TREE_LOCATION && (
                  <CreateTicketModalDecisionTreeSteps.SelectTreeLocation
                    onSelectTicketLocation={handleSelectLocation}
                  />
                )}
                {step === CreateTicketModalStep.SELECT_TREE_ROOT_NODE && (
                  <CreateTicketModalDecisionTreeSteps.SelectTreeRootNode onSelectNode={handleSelectNode} />
                )}
                {step === CreateTicketModalStep.VIEW_POTENTIAL_DUPLICATE_TICKETS && (
                  <CreateTicketModalSharedSteps.ViewPotentialDuplicateTickets />
                )}
                {step === CreateTicketModalStep.SELECT_TREE_NODE && (
                  <CreateTicketModalDecisionTreeSteps.SelectTreeNode onSelectNode={handleSelectNode} />
                )}
                {step === CreateTicketModalStep.ADD_ATTACHMENTS && <CreateTicketModalSharedSteps.AddAttachments />}
                {step === CreateTicketModalStep.ADD_INFORMATION && <CreateTicketModalSharedSteps.AddInformation />}
                {step === CreateTicketModalStep.ADD_CONTACT_INFORMATION && (
                  <CreateTicketModalSharedSteps.AddContactInformation />
                )}
                {step === CreateTicketModalStep.CONFIRM && <CreateTicketModalSharedSteps.Confirm />}
                {step === CreateTicketModalStep.APPOINTMENT_LOADING && (
                  <CreateTicketModalAppointmentSteps.LoadingMoments preparation={appointmentSelection?.preparation} />
                )}
                {step === CreateTicketModalStep.APPOINTMENT_PICK_MOMENT && (
                  <CreateTicketModalAppointmentSteps.PickMoment
                    options={appointmentOptions}
                    selectedSlotId={appointmentSelectedSlotId}
                    onSelectSlot={(slotId) => {
                      setAppointmentSelectedSlotId(slotId);
                      setIsAppointmentMomentUnavailableShown(false);
                    }}
                    isMomentUnavailableShown={isAppointmentMomentUnavailableShown}
                    onDismissMomentUnavailable={() => setIsAppointmentMomentUnavailableShown(false)}
                  />
                )}
                {step === CreateTicketModalStep.APPOINTMENT_PREVIEW && (
                  <CreateTicketModalAppointmentSteps.Preview
                    selectedOption={appointmentSelectedOption}
                    planningState={appointmentPreparationFailed ? "failed" : appointmentPlanning?.state}
                  />
                )}
              </section>
            )}
          </Form.Root>
        </Modal.Body>
        <Modal.Actions isSticky>
          {step !== defaultStep &&
            step !== CreateTicketModalStep.APPOINTMENT_LOADING &&
            step !== CreateTicketModalStep.APPOINTMENT_PICK_MOMENT &&
            step !== CreateTicketModalStep.APPOINTMENT_PREVIEW && (
              <span className="mr-auto w-fit">
                <Button variant="outline" onClick={handleClickBack}>
                  {t("common.action.back")}
                </Button>
              </span>
            )}
          {step === CreateTicketModalStep.SELECT_REPORTER && (
            <Button data-testid="continue-btn" onClick={handleConfirmSelectReporter}>
              {t("common.action.continue")}
            </Button>
          )}
          {step === CreateTicketModalStep.VIEW_POTENTIAL_DUPLICATE_TICKETS && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                {t("page.resident-tickets.create-modal.btn.found-existing-ticket")}
              </Button>
              <Button data-testid="continue-btn" onClick={handleIgnorePotentialDuplicateTickets}>
                {t("common.action.continue")}
              </Button>
            </>
          )}
          {step === CreateTicketModalStep.ADD_ATTACHMENTS && (
            <Button
              data-testid="continue-btn"
              disabled={currCategory?.categoryInfo?.requireImages && currImages.length === 0}
              onClick={handleConfirmAddImages}
            >
              {!currCategory?.categoryInfo?.requireImages &&
                currImages.length === 0 &&
                currVideos.length === 0 &&
                t("common.action.skip")}
              {currCategory?.categoryInfo?.requireImages && currImages.length === 0 && t("common.action.continue")}
              {(currImages.length > 0 || currVideos.length > 0) &&
                t("page.resident-tickets.create-modal.btn.continue-with-attachments", {
                  count: currImages.length + currVideos.length,
                })}
            </Button>
          )}
          {step === CreateTicketModalStep.ADD_INFORMATION && (
            <Button data-testid="continue-btn" onClick={handleConfirmAddInformation}>
              {t("common.action.continue")}
            </Button>
          )}
          {step === CreateTicketModalStep.ADD_CONTACT_INFORMATION && (
            <Button data-testid="continue-btn" onClick={handleConfirmAddContactInformation}>
              {t("common.action.continue")}
            </Button>
          )}
          {step === CreateTicketModalStep.CONFIRM && (
            <Button form={FORM_ID} type="submit" isLoading={addTicketMutation.isPending || isUploadingImage}>
              {t("page.resident-tickets.create-modal.btn.submit")}
            </Button>
          )}
          {step === CreateTicketModalStep.APPOINTMENT_PICK_MOMENT && (
            <>
              <span className="mr-auto w-fit">
                <Button variant="outline" onClick={handleSkipAppointmentMoment}>
                  {t("page.resident-tickets.create-modal.appointment.pick.skip")}
                </Button>
              </span>
              <Button
                data-testid="jh-confirm-moment-btn"
                disabled={!appointmentSelectedSlotId}
                isLoading={addTicketMutation.isPending}
                onClick={handleConfirmAppointmentMoment}
              >
                {t("page.resident-tickets.create-modal.appointment.pick.confirm")}
              </Button>
            </>
          )}
          {step === CreateTicketModalStep.APPOINTMENT_PREVIEW && (
            <Button
              data-testid="jh-done-btn"
              isLoading={addTicketMutation.isPending || isUploadingImage}
              onClick={handleConfirmAppointmentPreview}
            >
              {t("page.resident-tickets.create-modal.appointment.preview.done")}
            </Button>
          )}
        </Modal.Actions>
        <AnimatePresence>
          {Boolean(currCategory?.tree) && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, transition: { duration: 0.1 } }}
              transition={{ ease: "easeInOut", duration: 0.2 }}
              className="z-10 w-full p-space-sm"
            >
              <ProgressBar progress={currProgress} />
            </motion.div>
          )}
        </AnimatePresence>
        <ConfirmModal
          tone="primary"
          isOpened={isAppointmentSkipDialogOpened}
          onOpenChange={setIsAppointmentSkipDialogOpened}
          title={t("page.resident-tickets.create-modal.appointment.skip-dialog.title")}
          description={t("page.resident-tickets.create-modal.appointment.skip-dialog.description")}
          isLoading={false}
          onResolve={() => setIsAppointmentSkipDialogOpened(false)}
          resolveBtnProps={{ text: t("page.resident-tickets.create-modal.appointment.skip-dialog.keep") }}
          onReject={handleConfirmSkipAppointmentMoment}
          rejectBtnProps={{
            text: t("page.resident-tickets.create-modal.appointment.skip-dialog.skip"),
            "data-testid": "jh-skip-confirm-btn",
          }}
        />
      </ErrorBoundary>
    </Modal.Root>
  );
}
